import {
  REQUIRED_FIELD_MESSAGE,
  MIN_LENGTH_POLICY_MESSAGE,
  PASSWORD_POLICY_SYMBOLS,
  UUID_POLICY,
  USERNAME_POLICY,
  IS_APP_MOCKED,
} from "@src/constants/constants";
import { isNumberString } from "class-validator";
import moment from "moment";

export default class AppUtils {
  static isMockedApp = IS_APP_MOCKED === "true";
  /**
   * @example generateTicks(0, 5, 1) //[0, 1, 2, 3, 4, 5]
   * @example generateTicks(6, 9, 0.5) //[6, 6.5, 7, 7.5, 8, 8.5, 9]
   */
  static generateTicks(start, end, interval) {
    return Array.from(
      { length: Math.floor((end - start) / interval) + 1 },
      (_, i) => start + i * interval,
    );
  }

  static startsWithAVowel(word) {
    if (!word) return false;
    return "AEIOU".includes(word.charAt(0).toUpperCase());
  }

  static base64decode(input) {
    const byteArray = Uint8Array.from(atob(input), (c) => c.charCodeAt(0));
    return new TextDecoder().decode(byteArray);
  }

  static base64encode(input) {
    try {
      // Handle Unicode characters properly
      return btoa(unescape(encodeURIComponent(input)));
    } catch (e) {
      console.error("Failed to encode to base64:", e);
      return null;
    }
  }

  static limitDecimals = (value, decimals) => {
    return parseFloat(value.toFixed(decimals));
  };

  static isValidUUID = (val) => {
    return UUID_POLICY.test(val);
  };

  static isValidEmail = (val) => {
    const isEmail = true;
    const emailRegex = USERNAME_POLICY(isEmail);
    return emailRegex.test(val.trim().toLowerCase());
  };

  static countDecimals = (value) => {
    if (Math.floor(value) === value) return 0;

    var str = value.toString();
    if (str.indexOf(".") !== -1 && str.indexOf("-") !== -1) {
      return str.split("-")[1] || 0;
    } else if (str.indexOf(".") !== -1) {
      return str.split(".")[1].length || 0;
    }
    return str.split("-")[1] || 0;
  };

  static getPropTypeFromControls(controls) {
    return Object.keys(controls).reduce((acc, key) => {
      acc[key] = controls[key].propType;
      return acc;
    }, {});
  }

  /** Converts the controls to Storybook format
   * @example const meta = { ..., argTypes: AppUtils.getStorybookControls(checkboxControls)
   */
  static getStorybookControls(controls) {
    return Object.keys(controls).reduce((acc, key) => {
      acc[key] = {
        disable: controls[key].disable || false,
        control: {
          // All controlTypes: https://storybook.js.org/docs/essentials/controls#annotation
          type: controls[key].controlType,
          labels: controls[key].controlLabels,
        },
        options: controls[key].options || [],
        description: controls[key].description,
        defaultValue: controls[key].defaultValue || undefined,
      };
      return acc;
    }, {});
  }

  static getPasswordValidator = () => ({
    required: REQUIRED_FIELD_MESSAGE,
    //pattern: {
    //  value: PASSWORD_POLICY,
    //  message: PASSWORD_POLICY_MESSAGE
    //},
    validate: {
      // validate each rule individually for better UX
      upperCase: (v, l) => {
        if (l) {
          return "One uppercase character";
        }
        const errorMsg = "Please include at least one uppercase letter";
        const hasUpperCase = v.split("").some((c) => /[A-Z-À-Ü]/.test(c));
        return hasUpperCase || errorMsg;
      },
      lowerCase: (v, l) => {
        if (l) {
          return "One lowercase character";
        }
        const errorMsg = "Please include at least one lowercase letter";
        const hasLowerCase = v.split("").some((c) => /[a-z-à-ü]/.test(c));
        return hasLowerCase || errorMsg;
      },
      digits: (v, l) => {
        if (l) {
          return "One number";
        }
        const errorMsg = "Please include at least one number";
        const hasDigits = v.split("").some((c) => /\d/.test(c));
        return hasDigits || errorMsg;
      },
      specialCharacters: (v, l) => {
        if (l) {
          return "One special character";
        }
        const getAllowedSymbols = PASSWORD_POLICY_SYMBOLS;
        const errorMsg = `Please include one of the symbols allowed: ${getAllowedSymbols().join(
          "",
        )}`;
        const hasAnyRequiredSymbol = v
          .split("")
          .some((c) => getAllowedSymbols().includes(c));
        //const hasAnyRequiredSymbol = v.split('').some(c => /[@$!#%*?~+=&_\-\.\[\]\{\}\(\)]/.test(c));
        return hasAnyRequiredSymbol || errorMsg;
      },
      minLength: (v, l) => {
        if (l) {
          return "8 characters minimum";
        }
        const errorMsg = MIN_LENGTH_POLICY_MESSAGE(8);
        return v.length >= 8 || errorMsg;
      },
    },
  });

  static returnJourneyString = (origin, destination) => {
    if (
      (!origin || origin === "UNKNOWN") &&
      (!destination || destination === "UNKNOWN")
    ) {
      return "";
    }
    return `${
      !origin || origin === "UNKNOWN"
        ? `to ${destination}`
        : !destination || destination === "UNKNOWN"
          ? `from ${origin}`
          : `${origin} to ${destination}`
    }`;
  };

  static isMobile() {
    // Mobile screen width usually are smaller than 768px
    return window.innerWidth < 768;
  }

  static simulateResponse(response) {
    const delayMs = window.location.hostname === "localhost" ? 50 : 1500;
    return new Promise((resolve) => {
      console.debug("simulating response", response);
      setTimeout(() => {
        resolve(response);
      }, delayMs);
    });
  }

  static dateFancyFormatterUTC = (dateTimeUnix) => {
    return moment.utc(dateTimeUnix).format("Do MMM");
  };

  static datetimeTinyFormatterUTC = (dateTimeUnix) => {
    return moment.utc(dateTimeUnix).format("MMM DD, HH:mm");
  };

  static formatTime = (dateTimeUnix) => {
    return moment.utc(dateTimeUnix).format("HH:mm");
  };

  static sortDatetimes = (a, b) => {
    if (
      moment
        .utc(a, "MMM DD, hh:mm a")
        .isBefore(moment.utc(b, "MMM DD, hh:mm a"))
    ) {
      return 1;
    }
    return -1;
  };

  static sortNumbers = (a, b) => {
    const aNum = parseInt(a, 10);
    const bNum = parseInt(b, 10);
    if (isNaN(aNum)) return 1;
    if (isNaN(bNum)) return -1;
    return bNum - aNum;
  };

  /** case-insensitive sort for a string array */
  static sortStrings = (a, b) => {
    const aStr = a ?? "";
    const bStr = b ?? "";
    return aStr.localeCompare(bStr, undefined, { sensitivity: "base" });
  };

  static formatToTitleCase = (str) => {
    return str
      .replace(/_/g, " ")
      .replace(/\s+/g, " ")
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  static withIndefiniteArticle = (word) => {
    if (!word) return word;
    const article = /^[aeiou]/i.test(word) ? "an" : "a";
    return `${article} ${word}`;
  };

  static roundTo2DP = (n) => {
    return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
  };

  static isInOpenReportingRange = (
    dateToCheck,
    reportingDelay,
    targetPeriodDuration,
  ) => {
    const pStart = this.getRangeStart(reportingDelay, targetPeriodDuration);
    return moment.utc(dateToCheck).isSameOrAfter(pStart);
  };

  static getRangeStart(delay, pDuration) {
    const reportDelay = delay || 0;
    const lastUpdated = moment.utc().subtract(reportDelay, "days");
    const biweekly = pDuration === "BIWEEKLY";
    let rangeStart = moment.utc(lastUpdated).startOf("month");

    if (biweekly) {
      const dayOfMonth = lastUpdated.date();
      const isFebruary = lastUpdated.month() === 1;

      if (
        (dayOfMonth >= 16 && !isFebruary) ||
        (dayOfMonth >= 15 && isFebruary)
      ) {
        rangeStart.date(isFebruary ? 15 : 16);
      }
    }
    return rangeStart;
  }

  static getCurrentPeriod(date, industry) {
    if (!date || !industry) return null;

    const momentDate = moment.utc(date);
    const year = momentDate.year();
    const month = String(momentDate.month() + 1).padStart(2, "0");
    const day = momentDate.date();
    let periodKey;

    if (industry === "aviation") {
      periodKey = `${year}-${month}`;
    } else if (industry === "shipping") {
      if (month === "02") {
        const firstHalfEnd = 14;
        periodKey =
          day <= firstHalfEnd ? `${year}-${month}-01` : `${year}-${month}-15`;
      } else {
        periodKey = day <= 15 ? `${year}-${month}-01` : `${year}-${month}-15`;
      }
    } else {
      return null;
    }

    return this.getDateRangeFromPeriodKey(periodKey);
  }

  static getDateRangeFromPeriodKey(periodKey) {
    if (!periodKey) return null;

    // Check if the key is in YYYY-MM format (full month)
    if (/^\d{4}-\d{2}$/.test(periodKey)) {
      const monthStart = moment.utc(periodKey, "YYYY-MM");
      return {
        value: periodKey,
        from: monthStart.startOf("month").toDate(),
        fromTime: monthStart.startOf("month").valueOf(),
        to: monthStart.endOf("month").toDate(),
        toTime: monthStart.endOf("month").valueOf(),
        daysInPeriod: monthStart.daysInMonth(),
        isFullMonth: true,
      };
    }

    // Check if the key is in YYYY-MM-DD format (BIWEEKLY)
    // NOTE: "BIWEEKLY" is actually half-months: 1st-15th and 16th-End (or 1st-14th and 15th-End for February)
    if (/^\d{4}-\d{2}-\d{2}$/.test(periodKey)) {
      const date = moment.utc(periodKey, "YYYY-MM-DD");
      const monthStart = date.clone().startOf("month");
      const monthEnd = date.clone().endOf("month");

      // 2nd half starts on the 16th of each month (or 15th if it's February)
      const startOfSecondHalf = date.clone().date(date.month() === 1 ? 15 : 16);
      const endOfFirstHalf = startOfSecondHalf.clone().subtract(1, "second");

      const isFirstHalf = date.isBefore(startOfSecondHalf);
      const from = isFirstHalf ? monthStart : startOfSecondHalf;
      const to = isFirstHalf ? endOfFirstHalf : monthEnd;
      return {
        value: periodKey,
        from: from.toDate(),
        fromTime: from.valueOf(),
        to: to.toDate(),
        toTime: to.valueOf(),
        daysInPeriod: to.diff(from, "days") + 1, //+1 to include both start and end day
        isFullMonth: false,
        isFirstHalf,
      };
    }

    console.warn(
      "getDateRangeFromPeriodKey: could not parse periodKey",
      periodKey,
    );
    return null;
  }

  /** @returns {number | null} a millisecond timestamp representing the input date, null if invalid */
  static serializeDate(date) {
    if (date == null) return null;
    if (typeof date === "number") {
      if (String(date).length === 13) return date;
      if (String(date).length === 9) return date * 1000;
    }
    const m = moment.utc(isNumberString(date) ? Number(date) : date);
    return m.isValid() ? m.valueOf() : null;
  }
  static endOfMonthIfValidDate(date) {
    if (date == null || date === "all") return null;
    const m = moment.utc(isNumberString(date) ? Number(date) : date);
    if (m.isValid()) return m.endOf("month").valueOf();
    return undefined;
  }

  static getStateIcon(state) {
    const stateIcon = {
      EXCLUSION: "ban",
      INCOMPLETE: "question",
      SUCCESS: "check",
      FAIL: "close",
    }[state];
    return stateIcon;
  }

  static getShuffledArray = (array) => {
    const arrayClone = [...array];
    for (let i = arrayClone.length - 1; i >= 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arrayClone[i], arrayClone[j]] = [arrayClone[j], arrayClone[i]];
    }
    return arrayClone;
  };

  static isValidHttpUrl = (val) => {
    let url;
    let isValid = true;
    try {
      url = new URL(val);
      // eslint-disable-next-line no-unused-vars
    } catch (e) {
      isValid = false;
    }
    return isValid ? url.protocol.startsWith("http") : isValid;
  };

  static capitalizeFirstLetter = (str) => {
    if (!str) return str;
    return str
      .trim()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  static removeUnicodeCharacters = (str) => {
    if (!str) return str;
    return str.replace(/\\u[0-9a-fA-F]{4}/g, "");
  };

  static isShadedColor = (color) => {
    const withShade = /^.+-\d{2,3}$/.test(color);
    if (withShade) {
      const info = color.split("-");
      return {
        name: info[0],
        shade: parseInt(info[1], 10),
      };
    }
    return { name: color };
  };

  static isFeatureAvailableForCustomer = (featureID, CustomerCode) => {
    return this.isFeatureAvailable(featureID, CustomerCode);
  };

  static isFeatureAvailableForBehaviour = (featureID, BehaviourCode) => {
    return this.isFeatureAvailable(featureID, BehaviourCode);
  };

  static isFeatureAvailable = (featureID, key) => {
    const featureList = {
      OTInMeters: ["CAPE", "UTB", "NAVG"],
      MultiEngineESFOC: ["SEFE"],
      EfficientTrimRangeGraph: ["BSM", "CHR", "AET", "VSHP"],
      BehiveDashboards: ["VS", "UTB", "TUIC", "BSM", "NYK", "CHR"],
      BehaviourWithNoFuelSavings: ["HQDS"],
    };
    if (
      featureID &&
      featureList[featureID] &&
      featureList[featureID].length > 0
    ) {
      if (!featureList[featureID].includes(key)) {
        return false;
      }
    }
    return true;
  };

  static getMonthsSinceLaunchDate = (launchDate) => {
    const start = moment.utc(launchDate);
    const end = moment.utc().startOf("month");

    let startOfMonth = start.clone().startOf("month");
    const months = [startOfMonth.format()];
    let i = 1;
    while (true) {
      const startDate = start.clone().add(i, "months").startOf("month");
      if (startDate.isBefore(end)) {
        months.push(startDate.format());
        i++;
      } else break;
    }
    const startOfCurrentMonth = moment.utc().startOf("month").format();
    if (!months.includes(startOfCurrentMonth)) {
      months.push(startOfCurrentMonth);
    }
    return months;
  };

  static setUrlQueryParams(urlString, params) {
    const url = new URL(urlString);
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, value);
    }
    return url.toString();
  }

  static simplifyError(error, additionalData = {}) {
    const baseStack = new Error().stack;

    if (!error) {
      return { message: "Unknown error", stack: baseStack, ...additionalData };
    }

    if (
      error instanceof Error ||
      (typeof error === "object" && ("name" in error || "message" in error))
    ) {
      return {
        ...error,
        name: error.name || "Error",
        message: error.message || String(error),
        stack: error.stack || baseStack,
        ...(additionalData || {}),
      };
    }

    // Fallback for anything else (strings, numbers, unserializable objects)
    try {
      let message =
        typeof error === "object" ? JSON.stringify(error) : String(error);
      if (message === "{}") message = String(error);
      return { message, stack: baseStack, ...(additionalData || {}) };
    } catch {
      return {
        message: "[Unserializable object]",
        stack: baseStack,
        ...(additionalData || {}),
      };
    }
  }

  /**
   * Extract parameters from a SQL query for basic mode
   * Parses SELECT fields, FROM table, and WHERE date conditions
   * @param {string} sql - SQL query string
   * @returns {Object} Object with fields, from, and to properties
   */
  static extractParamsFromQuery(sql) {
    try {
      // Extract SELECT fields
      const fieldsMatch = sql.match(/SELECT\s+(.*?)\s+FROM/i);
      const fields = fieldsMatch
        ? fieldsMatch[1]
            .split(",")
            .map((f) => f.trim())
            .filter((f) => f && f !== "*")
        : [];

      // Extract FROM date (>= condition)
      const fromMatch = sql.match(/>=\s*'(\d{4}-\d{2}-\d{2})'/);
      const from = fromMatch ? fromMatch[1] : null;

      // Extract TO date (<= condition)
      const toMatch = sql.match(/<=\s*'(\d{4}-\d{2}-\d{2})'/);
      const to = toMatch ? toMatch[1] : null;

      return {
        fields,
        from,
        to,
      };
    } catch (e) {
      console.error("Failed to parse SQL query:", e);
      return {
        fields: [],
        from: null,
        to: null,
      };
    }
  }
}
